import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.rollm8s.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const SITE_NAME = 'RollM8s';

const DEFAULT_KEYWORDS = [
  'roller skating events UK',
  'skater ride share',
  'skateboard events UK',
  'BMX events UK',
  'inline skating events UK',
  'scooter events UK',
  'longboard events UK',
  'snow sports events UK',
  'skating events near me',
  'roller disco UK',
  'rollerskating community',
  'skate jam events',
  'roller skating meetups',
  'skate night UK',
  'roller skate ride share',
  'skating event tickets',
  'UK skate wheels boards community',
  'roller skating lifts',
  'skate event carpooling',
  'quad skating events',
  'roller rink events',
  'skate community platform',
  'roller skating tickets online',
  'find skating events',
  'skate event near me'
].join(', ');

export const SEO = ({
  title,
  description,
  path = '',
  image,
  keywords,
  type = 'website',
  noindex = false,
  children
}) => {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - UK Skate, Wheels & Boards Community`;
  const pageDescription = description || "Connect with the UK Skate, Wheels & Boards community. Share rides to events, split petrol costs, find skate, board, BMX, scooter and snow sports events near you.";
  const pageUrl = `${SITE_URL}${path}`;
  const pageImage = image || DEFAULT_IMAGE;
  const pageKeywords = keywords ? `${keywords}, ${DEFAULT_KEYWORDS}` : DEFAULT_KEYWORDS;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={pageUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {children}
    </Helmet>
  );
};

export const EventSEO = ({ event }) => {
  if (!event) return null;

  const title = event.name || event.title;
  const description = event.description
    ? event.description.substring(0, 160)
    : `${title} - Get tickets and find rides to this skating event on RollM8s.`;
  const image = event.flyer_url || event.image;
  const path = `/events/${event._id || event.id}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: title,
    description: description,
    url: `https://www.rollm8s.com${path}`,
    ...(image && { image: [image] }),
    ...(event.date && { startDate: event.date }),
    ...(event.end_date && { endDate: event.end_date }),
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: event.status === 'cancelled'
      ? 'https://schema.org/EventCancelled'
      : 'https://schema.org/EventScheduled',
    organizer: {
      '@type': 'Organization',
      name: event.organiser_name || 'RollM8s',
      url: 'https://www.rollm8s.com'
    },
    ...(event.venue_name && {
      location: {
        '@type': 'Place',
        name: event.venue_name,
        ...(event.address && {
          address: {
            '@type': 'PostalAddress',
            streetAddress: event.address
          }
        })
      }
    }),
    ...(event.ticket_releases && event.ticket_releases.length > 0 && {
      offers: event.ticket_releases
        .filter(r => r.price_pence !== undefined)
        .map(release => ({
          '@type': 'Offer',
          name: release.name || 'General Admission',
          price: (release.price_pence / 100).toFixed(2),
          priceCurrency: 'GBP',
          availability: release.sold >= release.quantity
            ? 'https://schema.org/SoldOut'
            : 'https://schema.org/InStock',
          url: `https://www.rollm8s.com${path}`,
          validFrom: release.start_date || event.created_at
        }))
    })
  };

  return (
    <SEO
      title={title}
      description={description}
      path={path}
      image={image}
      type="website"
      keywords={`${title}, skating event, roller skating, ${event.venue_name || ''}, ${event.city || ''}`}
    >
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </SEO>
  );
};

export const OrganizationJsonLd = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'RollM8s',
    url: 'https://www.rollm8s.com',
    logo: 'https://www.rollm8s.com/rollm8s-logo-dark.png',
    description: "UK Skate, Wheels & Boards community platform. Connecting roller skaters, inline skaters, skateboarders, BMX, scooter and snow sports communities with rides to events across the UK.",
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: 'https://www.rollm8s.com/support'
    },
    areaServed: {
      '@type': 'Country',
      name: 'United Kingdom'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export const WebsiteJsonLd = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'RollM8s',
    url: 'https://www.rollm8s.com',
    description: "UK Skate, Wheels & Boards community platform connecting riders with rides to events.",
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://www.rollm8s.com/events?search={search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};
